/* Shared, bounded image work. Only fully decoded images replace the visible photograph. */
(() => {
  if (window.OnaInstantMedia) return;
  const cache = new Map(), queue = [];
  let active = 0, background = 0;
  const lowData = () => navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '');
  const url = (value, root, width) => {
    if (!value) return '';
    const result = new URL(value, location.origin);
    if (!['https:', 'http:'].includes(result.protocol)) return '';
    // The product stage starts hidden; measure its visible container before choosing pixels.
    let measuring = root, cssWidth = measuring?.getBoundingClientRect().width || 0;
    while (measuring && !cssWidth) { measuring = measuring.parentElement; cssWidth = measuring?.getBoundingClientRect().width || 0; }
    const pixels = width || Math.max(320, (cssWidth || 480) * Math.min(devicePixelRatio || 1, 2));
    const bucket = [320, 480, 640, 800, 960, 1200, 1600, 2048, 2560].find(size => size >= pixels) || 2560;
    if (result.hostname.endsWith('shopify.com')) result.searchParams.set('width', String(bucket));
    return result.href;
  };
  const trim = () => {
    for (const [key, record] of cache) {
      if (cache.size <= 24) break;
      if (record.status === 'ready') cache.delete(key);
    }
  };
  const run = () => {
    while (active < 4) {
      const index = queue.findIndex(record => record.priority === 'high' || background < 2);
      if (index < 0) break;
      const record = queue.splice(index, 1)[0];
      active++; if (record.priority !== 'high') background++;
      const wasBackground = record.priority !== 'high';
      record.status = 'loading';
      const image = record.image = new Image();
      image.decoding = 'async'; image.fetchPriority = record.priority;
      const finish = () => { active--; if (wasBackground) background--; trim(); run(); };
      image.onload = async () => {
        try {
          if (image.decode) await image.decode();
          if (!image.naturalWidth) throw new Error('Empty image');
          record.status = 'ready'; record.resolve(record);
        } catch (error) { cache.delete(record.key); record.reject(error); }
        finally { finish(); }
      };
      image.onerror = () => { cache.delete(record.key); record.reject(new Error('Image unavailable')); finish(); };
      image.src = record.key;
    }
  };
  const load = (value, root, priority = 'high') => {
    const key = url(value, root); if (!key) return Promise.reject(new Error('Missing image'));
    let record = cache.get(key);
    if (record) {
      cache.delete(key); cache.set(key, record);
      if (priority === 'high') { record.priority = 'high'; if (record.image) record.image.fetchPriority = 'high'; run(); }
      return record.promise;
    }
    record = { key, priority, status: 'queued' };
    record.promise = new Promise((resolve, reject) => { record.resolve = resolve; record.reject = reject; });
    cache.set(key, record);
    if (priority === 'high') queue.unshift(record); else queue.push(record);
    run(); return record.promise;
  };
  const seed = (image, value, root) => {
    if (!image?.complete || !image.naturalWidth || !value) return;
    const shown = image.currentSrc || image.src;
    if (!shown || new URL(shown, location.origin).pathname !== new URL(value, location.origin).pathname) return;
    const key = url(value, root); if (cache.has(key)) return;
    // A thumbnail cannot seed the decoded cache for a larger display request.
    const delivered = new URL(shown, location.origin);
    const requested = new URL(key, location.origin);
    const deliveredWidth = Number(delivered.searchParams.get('width')) || image.naturalWidth;
    const requestedWidth = Number(requested.searchParams.get('width')) || deliveredWidth;
    if (deliveredWidth < requestedWidth) return;
    const record = { key, image, status: 'ready', priority: 'high' };
    record.promise = Promise.resolve(record); cache.set(key, record); trim();
  };
  const prepare = async (value, root, current) => {
    seed(current, value, root);
    const record = await load(value, root);
    const image = new Image(); image.decoding = 'async'; image.src = record.image.currentSrc || record.image.src;
    if (image.decode) await image.decode();
    return image;
  };
  const mount = (current, image, alt) => {
    if (current) {
      for (const attribute of [...current.attributes]) {
        if (!['src', 'srcset', 'sizes', 'loading', 'decoding', 'fetchpriority'].includes(attribute.name)) image.setAttribute(attribute.name, attribute.value);
      }
      current.replaceWith(image);
    }
    if (alt !== undefined) image.alt = alt;
    image.loading = 'eager'; image.hidden = false;
    return image;
  };
  const warm = (values, root) => {
    if (lowData()) return;
    [...new Set(values.filter(Boolean))].forEach(value => load(value, root, 'low').catch(() => {}));
  };
  const watch = (root, values, sizingRoot = root) => {
    let idle, stopped = false;
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      const work = () => { if (!stopped && root.isConnected) warm(values(), sizingRoot); };
      if ('requestIdleCallback' in window) idle = requestIdleCallback(work, { timeout: 1000 }); else idle = setTimeout(work, 0);
    }, { rootMargin: '250px' });
    observer.observe(root);
    return () => { stopped = true; observer.disconnect(); if ('cancelIdleCallback' in window) cancelIdleCallback(idle); else clearTimeout(idle); };
  };
  // Hover intent can warm Dawn's exact section response; server data remains authoritative.
  const sections = new Map();
  const prefetchSection = requestUrl => {
    if (!requestUrl || lowData()) return;
    const existing = sections.get(requestUrl);
    if (existing && Date.now() - existing.at < 10000) return;
    if ([...sections.values()].filter(record => record.pending).length >= 2) return;
    const record = { at: Date.now(), pending: true };
    record.promise = fetch(requestUrl, { credentials: 'same-origin', priority: 'low' })
      .then(response => { if (!response.ok) throw new Error('Options unavailable'); return response.text(); })
      .then(html => { if (!html.includes('<product-info')) throw new Error('Options unavailable'); record.pending = false; return html; })
      .catch(error => { sections.delete(requestUrl); throw error; });
    record.promise.catch(() => {}); sections.set(requestUrl, record);
    if (sections.size > 10) sections.delete(sections.keys().next().value);
  };
  window.OnaInstantMedia = { url, prepare, mount, warm, watch, seed,
    sections: { prefetch: prefetchSection, take: requestUrl => {
      const record = sections.get(requestUrl);
      if (!record || Date.now() - record.at > 10000) return null;
      sections.delete(requestUrl); return record.promise;
    } }
  };
})();
